import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';

// ─── STATUS COLORS ─────────────────────────────────────────
const STATUS_COLORS = {
  running: '#22C55E',
  inactive: '#F59E0B',
  stopped: '#EF4444'
};

const STATUS_GLOW = {
  running: '#22C55E',
  inactive: '#F59E0B',
  stopped: '#EF4444'
};

// ─── MACHINE STATUS OVERLAY ────────────────────────────────
// Floating HUD above a machine showing real-time data

export function MachineStatusOverlay({ position, data, showDetails = true, machineId, onOpen }) {
  if (!data) return null;

  const color = STATUS_COLORS[data.status] || STATUS_COLORS.inactive;
  const bgColor = data.isRunning ? 'rgba(10, 30, 10, 0.92)' : 'rgba(30, 20, 10, 0.92)';
  const handleClick = () => { if (onOpen && machineId) onOpen(machineId); };

  return (
    <Html position={position} center>
      <div
        onClick={handleClick}
        style={{
          background: bgColor,
          border: `2px solid ${color}`,
          borderRadius: 8,
          padding: '6px 12px',
          minWidth: 120,
          color: '#FFF',
          fontSize: 10,
          fontFamily: 'monospace',
          userSelect: 'none',
          cursor: onOpen ? 'pointer' : 'default',
          boxShadow: `0 0 12px ${color}40`
        }}
      >
        {/* Status dot + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: color,
              boxShadow: `0 0 6px ${color}`,
              animation: data.isRunning ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }}
          />
          <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
            {data.status}
          </span>
          {data.mode && data.mode !== 'UNKNOWN' && (
            <span style={{ opacity: 0.7, fontSize: 9 }}>
              {data.mode}
            </span>
          )}
        </div>

        {showDetails && (
          <>
            {/* Speed */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ opacity: 0.6 }}>Speed</span>
              <span style={{ color: data.speed > 0 ? '#4ADE80' : '#888', fontWeight: 600 }}>
                {data.speed.toFixed(1)} m/min
              </span>
            </div>

            {/* Meter */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ opacity: 0.6 }}>Meter</span>
              <span style={{ fontWeight: 600 }}>
                {data.meterAct.toFixed(0)} m
              </span>
            </div>

            {/* Total */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ opacity: 0.6 }}>Total</span>
              <span style={{ fontWeight: 600 }}>
                {(data.meterTotal / 1000).toFixed(2)} km
              </span>
            </div>

            {/* Operator */}
            {data.operator && data.operator !== 'Unknown' && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 2, marginTop: 2 }}>
                <span style={{ opacity: 0.6 }}>Op</span>
                <span>{data.operator}</span>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </Html>
  );
}

// ─── STATUS LIGHT (simple beacon on top of machine) ────────

export function StatusLight({ position, status = 'inactive', size = 0.15 }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.inactive;
  const ref = useRef();

  useFrame(({ clock }) => {
    if (ref.current && status === 'running') {
      ref.current.material.emissiveIntensity = 0.5 + Math.sin(clock.elapsedTime * 3) * 0.3;
    }
  });

  return (
    <mesh ref={ref} position={position}>
      <sphereGeometry args={[size, 12, 12]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={status === 'running' ? 0.8 : 0.3}
        roughness={0.3}
      />
    </mesh>
  );
}

// ─── ROTATING DRUM (animated when running) ─────────────────

export function AnimatedDrum({ position, rotation = [0, 0, 0], radius = 0.4, height = 0.5, speed = 0, color = '#7A6A5A' }) {
  const ref = useRef();

  useFrame(() => {
    if (ref.current && speed > 0) {
      ref.current.rotation.z += speed * 0.002;
    }
  });

  return (
    <mesh ref={ref} position={position} rotation={rotation}>
      <cylinderGeometry args={[radius, radius, height, 16]} />
      <meshStandardMaterial color={color} roughness={0.6} />
    </mesh>
  );
}

// ─── DTB PAY-OFF STATUS INDICATORS ─────────────────────────

export function PayoffIndicators({ position, payoffs = [] }) {
  if (!payoffs.length) return null;

  return (
    <Html position={position} center>
      <div
        style={{
          background: 'rgba(10, 10, 30, 0.9)',
          border: '1px solid #666',
          borderRadius: 6,
          padding: '4px 8px',
          display: 'flex',
          gap: 4,
          userSelect: 'none',
          pointerEvents: 'none'
        }}
      >
        {payoffs.map(po => (
          <div
            key={po.num}
            style={{
              width: 18,
              height: 18,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: '#FFF',
              background: po.emergency
                ? '#EF4444'
                : po.active
                ? '#22C55E'
                : '#555',
              boxShadow: po.active ? '0 0 6px #22C55E80' : 'none'
            }}
          >
            {po.num}
          </div>
        ))}
      </div>
    </Html>
  );
}

// ─── L75 MODE INDICATOR (Capstan vs Caterpillar) ───────────

export function L75ModeIndicator({ position, data }) {
  if (!data) return null;

  return (
    <Html position={position} center>
      <div
        style={{
          background: 'rgba(10, 10, 30, 0.9)',
          border: '1px solid #3874ff',
          borderRadius: 6,
          padding: '4px 10px',
          fontSize: 10,
          fontFamily: 'monospace',
          color: '#FFF',
          userSelect: 'none',
          pointerEvents: 'none',
          display: 'flex',
          gap: 10
        }}
      >
        <span style={{ color: data.capstanOn ? '#4ADE80' : '#555' }}>
          ● Capstan
        </span>
        <span style={{ color: data.e75On ? '#4ADE80' : '#555' }}>
          ● E75
        </span>
        <span style={{ color: data.e45On ? '#4ADE80' : '#555' }}>
          ● E45
        </span>
      </div>
    </Html>
  );
}
